<script setup lang="ts">
/**
 * A row of seven round day chips (Mo Di Mi … So) to pick one weekday or
 * several. Days count like JavaScript and cron — 0 is Sunday — and the row
 * starts at `firstDay` (Monday by default). Names come from `Intl` in the
 * app's locale; the chip shows the short form, assistive tech hears the long
 * one. `NX_WEEKDAY_CODES` maps a day to its RRULE code.
 *
 * - `multiple` off: `v-model` is one day or `null`, the row is a radiogroup
 *   and the arrow keys move the choice (the radio pattern).
 * - `multiple` on: `v-model` is an array in display order, the row is a group
 *   of toggle buttons (`aria-pressed`); the arrow keys only move focus, Space
 *   or Enter toggles.
 *
 * One chip is a tab stop (roving focus), so the row costs one Tab. Days in
 * `disabledDays` stay visible but cannot be picked; weekends are set a shade
 * quieter while unpicked.
 */
import { computed, inject, ref } from 'vue';
import { i18nInjectionKey } from '../internal';
import { weekdayNames, weekdayOrder, type NxWeekday } from './weekdays';

const props = withDefaults(
  defineProps<{
    multiple?: boolean;
    /** First chip of the row: 1 = Monday (default), 0 = Sunday, 6 = Saturday. */
    firstDay?: number;
    /** BCP 47 locale for the names; the app's locale when omitted. */
    locale?: string;
    disabled?: boolean;
    /** Days that are shown but cannot be picked. */
    disabledDays?: readonly number[];
    /** Accessible name of the row; defaults to „Wochentag“ / „Wochentage“. */
    ariaLabel?: string;
    /** `narrow` shows one letter per chip (M D M D F S S). */
    format?: 'short' | 'narrow';
    size?: 'sm' | 'md';
  }>(),
  {
    multiple: false,
    firstDay: 1,
    locale: undefined,
    disabled: false,
    disabledDays: () => [],
    ariaLabel: undefined,
    format: 'short',
    size: 'md',
  }
);

const model = defineModel<number | number[] | null>({ default: null });

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de-DE');

const order = computed(() => weekdayOrder(props.firstDay));
const shortNames = computed(() => weekdayNames(locale.value, props.format));
const longNames = computed(() => weekdayNames(locale.value, 'long'));
const groupLabel = computed(() => props.ariaLabel ?? (props.multiple ? 'Wochentage' : 'Wochentag'));

const picked = computed<Set<number>>(() => {
  const value = model.value;
  if (Array.isArray(value)) return new Set(value);
  return value === null || value === undefined ? new Set() : new Set([value]);
});

function isOn(day: NxWeekday): boolean {
  return picked.value.has(day);
}
function isBlocked(day: NxWeekday): boolean {
  return props.disabled || props.disabledDays.includes(day);
}

/** The one chip that is a tab stop: the focused one, else the first picked, else the first free. */
const focusIndex = ref<number | null>(null);
const tabStop = computed(() => {
  if (focusIndex.value !== null) return focusIndex.value;
  const pickedAt = order.value.findIndex((d) => isOn(d) && !isBlocked(d));
  if (pickedAt >= 0) return pickedAt;
  return Math.max(
    0,
    order.value.findIndex((d) => !isBlocked(d))
  );
});

function pick(day: NxWeekday) {
  if (isBlocked(day)) return;
  if (!props.multiple) {
    model.value = day;
    return;
  }
  const next = new Set(picked.value);
  if (next.has(day)) next.delete(day);
  else next.add(day);
  model.value = order.value.filter((d) => next.has(d));
}

const root = ref<HTMLElement | null>(null);

function onKeydown(event: KeyboardEvent, index: number) {
  const step =
    event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
  let target = -1;
  if (step !== 0) {
    for (let i = 1; i <= 7; i++) {
      const at = (index + step * i + 7 * 7) % 7;
      if (!isBlocked(order.value[at]!)) {
        target = at;
        break;
      }
    }
  } else if (event.key === 'Home') {
    target = order.value.findIndex((d) => !isBlocked(d));
  } else if (event.key === 'End') {
    for (let at = 6; at >= 0 && target < 0; at--) if (!isBlocked(order.value[at]!)) target = at;
  }
  if (target < 0) return;
  event.preventDefault();
  focusIndex.value = target;
  root.value?.querySelectorAll<HTMLButtonElement>('button')[target]?.focus();
  // The radio pattern: in a radiogroup, moving IS choosing.
  if (!props.multiple) pick(order.value[target]!);
}

function onFocusOut(event: FocusEvent) {
  const next = event.relatedTarget as Node | null;
  if (!next || !(event.currentTarget as HTMLElement).contains(next)) focusIndex.value = null;
}
</script>

<template>
  <div
    ref="root"
    class="nx-weekday-picker"
    :class="[`size-${props.size}`, { 'is-disabled': props.disabled }]"
    :role="props.multiple ? 'group' : 'radiogroup'"
    :aria-label="groupLabel"
    :aria-disabled="props.disabled || undefined"
    data-testid="nx-weekday-picker"
    @focusout="onFocusOut"
  >
    <button
      v-for="(day, index) in order"
      :key="day"
      type="button"
      class="nx-weekday-picker__day"
      :class="{ 'is-on': isOn(day), 'is-weekend': day === 0 || day === 6 }"
      :role="props.multiple ? undefined : 'radio'"
      :aria-checked="props.multiple ? undefined : isOn(day)"
      :aria-pressed="props.multiple ? isOn(day) : undefined"
      :aria-label="longNames[day]"
      :title="longNames[day]"
      :disabled="isBlocked(day)"
      :tabindex="index === tabStop ? 0 : -1"
      :data-day="day"
      @click="pick(day)"
      @focus="focusIndex = index"
      @keydown="onKeydown($event, index)"
    >
      {{ shortNames[day] }}
    </button>
  </div>
</template>

<style scoped>
.nx-weekday-picker {
  --weekday-size: var(--nxt-control-size-md);

  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
}
.nx-weekday-picker.size-sm {
  --weekday-size: var(--nxt-control-size-sm);
}

.nx-weekday-picker__day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--weekday-size);
  height: var(--weekday-size);
  padding: 0 var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1;
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.size-md .nx-weekday-picker__day {
  font-size: var(--nxt-font-size-sm);
}
.nx-weekday-picker__day.is-weekend:not(.is-on) {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-subtle);
}
.nx-weekday-picker__day:hover:not(:disabled, .is-on) {
  border-color: var(--nxt-border-strong);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-weekday-picker__day:active:not(:disabled) {
  transform: scale(0.94);
}
.nx-weekday-picker__day.is-on {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-weekday-picker__day.is-on:hover:not(:disabled) {
  border-color: var(--nxt-brand-bg-hover);
  background: var(--nxt-brand-bg-hover);
}
.nx-weekday-picker__day:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-weekday-picker__day:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .nx-weekday-picker__day {
    transition: none;
  }
  .nx-weekday-picker__day:active:not(:disabled) {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-weekday-picker__day {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-weekday-picker__day:active:not(:disabled) {
  transform: none;
}
</style>
